import React from 'react';
import { Helmet } from 'react-helmet-async';
import { DownloadIcon, KeyIcon, LogInIcon, HomeIcon, CalendarIcon, CheckCircleIcon, LineChartIcon, BellIcon, CreditCardIcon, BookOpenIcon, PencilIcon, VideoIcon, AppleIcon, SmartphoneIcon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const TotalDrivePage = () => {
  return <div className="bg-white" data-id="element-2116">
      <Helmet data-id="element-2117">
        <title data-id="element-2118">Total Drive App Guide | Northampton Driving School</title>
        <meta name="description" content="Learn how to use the Total Drive app for booking driving lessons in Northampton, tracking progress, making payments, and more with our step-by-step guide." data-id="element-2119" />
        <meta name="keywords" content="Total Drive app, driving lesson app, Northampton driving lessons, book driving lessons online, driving lesson progress tracker, driving lesson payment app" data-id="element-2120" />
      </Helmet>
      <div className="container mx-auto px-4 py-12" data-id="element-2121">
        <h1 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2122">
          Total Drive App Guide
        </h1>
        {/* Intro Section */}
        <section className="mb-12 bg-gray-50 p-6 rounded-lg border border-gray-200" data-id="element-2123">
          <h2 className="text-2xl font-bold mb-4" data-id="element-2124">
            Simplify Your Learning Journey
          </h2>
          <p className="text-gray-700 mb-4" data-id="element-2125">
            At Northampton Driving School, we use the Total Drive app to make
            learning simple and stress-free. With the app, you can:
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-id="element-2126">
            <li className="flex items-start" data-id="element-2127">
              <CalendarIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2128" />
              <span data-id="element-2129">Book your driving lessons in Northampton</span>
            </li>
            <li className="flex items-start" data-id="element-2130">
              <LineChartIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2131" />
              <span data-id="element-2132">Track your progress with your instructor</span>
            </li>
            <li className="flex items-start" data-id="element-2133">
              <CreditCardIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2134" />
              <span data-id="element-2135">Pay for lessons securely</span>
            </li>
            <li className="flex items-start" data-id="element-2136">
              <BellIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2137" />
              <span data-id="element-2138">Receive notifications and reminders</span>
            </li>
          </ul>
          <p className="text-gray-700" data-id="element-2139">
            Follow this step-by-step guide with screenshots to get started.
          </p>
        </section>
        {/* Step-by-Step Guide */}
        <div className="space-y-12" data-id="element-2140">
          {/* Step 1 */}
          <section id="step1" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2141">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2142">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2143">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2144">
                  1
                </span>
                Download the Total Drive App
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2145">
              <div data-id="element-2146">
                <p className="mb-4" data-id="element-2147">Download from:</p>
                <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-2148">
                  <a href="https://apps.apple.com/gb/app/total-drive/id1446269375" target="_blank" rel="noopener noreferrer" className="flex items-center" data-id="element-2149">
                    <img src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg" alt="Download on the App Store" className="h-12" data-id="element-2150" />
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.totaldrive.com.totaldrive&hl=en_GB" target="_blank" rel="noopener noreferrer" className="flex items-center" data-id="element-2151">
                    <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" className="h-14" data-id="element-2152" />
                  </a>
                </div>
                <p className="mb-4" data-id="element-2153">
                  <span className="font-medium" data-id="element-2154">1.</span> Open the app and
                  select <strong data-id="element-2155">Pupil / Parent</strong>.
                </p>
              </div>
              <div className="flex justify-center" data-id="element-2156">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fchFF5RWyasy6zz1WoRWeZ/01._Step_1.jpg" alt="Total Drive App Welcome Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2157" />
              </div>
            </div>
          </section>
          {/* Step 2 */}
          <section id="step2" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2158">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2159">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2160">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2161">
                  2
                </span>
                Enter Your Driving School PIN
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2162">
              <div data-id="element-2163">
                <p className="mb-4" data-id="element-2164">
                  <span className="font-medium" data-id="element-2165">1.</span> Enter the 4-digit PIN
                  sent by your instructor.
                </p>
                <p className="mb-4" data-id="element-2166">
                  <span className="font-medium" data-id="element-2167">2.</span> Tap{' '}
                  <strong data-id="element-2168">Continue</strong>.
                </p>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6" data-id="element-2169">
                  <p className="text-sm text-yellow-800" data-id="element-2170">
                    <strong data-id="element-2171">Tip:</strong> Don't have a PIN? Contact your
                    Northampton Driving School instructor to get your unique
                    code.
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2172">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8kd2oG1RPMEfFy7oPFtUzC/01._Step_2.jpg" alt="Total Drive App PIN Entry Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2173" />
              </div>
            </div>
          </section>
          {/* Step 3 */}
          <section id="step3" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2174">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2175">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2176">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2177">
                  3
                </span>
                Log In to Your Account
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2178">
              <div data-id="element-2179">
                <p className="mb-4" data-id="element-2180">
                  <span className="font-medium" data-id="element-2181">1.</span> Enter your mobile
                  number and password (provided via text).
                </p>
                <p className="mb-4" data-id="element-2182">
                  <span className="font-medium" data-id="element-2183">2.</span> Tap{' '}
                  <strong data-id="element-2184">Login</strong> and accept the terms and conditions.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6" data-id="element-2185">
                  <p className="text-sm text-blue-800" data-id="element-2186">
                    <strong data-id="element-2187">Note:</strong> If you haven't received your login
                    details, please contact your instructor.
                  </p>
                </div>
              </div>
              <div className="flex justify-center gap-4" data-id="element-2188">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/87gBv1txfEfPqrMaoh2FjX/03._Step_3.jpg" alt="Total Drive App Login Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2189" />
              </div>
            </div>
          </section>
          {/* Step 4 */}
          <section id="step4" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2190">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2191">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2192">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2193">
                  4
                </span>
                Explore the Home Screen
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2194">
              <div data-id="element-2195">
                <p className="mb-4" data-id="element-2196">On the home screen, you can:</p>
                <ul className="space-y-3 mb-6" data-id="element-2197">
                  <li className="flex items-start" data-id="element-2198">
                    <CalendarIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2199" />
                    <span data-id="element-2200">View your next driving lesson at a glance</span>
                  </li>
                  <li className="flex items-start" data-id="element-2201">
                    <CreditCardIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2202" />
                    <span data-id="element-2203">Pay for lessons</span>
                  </li>
                  <li className="flex items-start" data-id="element-2204">
                    <BookOpenIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2205" />
                    <span data-id="element-2206">Access learning resources</span>
                  </li>
                  <li className="flex items-start" data-id="element-2207">
                    <CalendarIcon className="text-burgundy-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-2208" />
                    <span data-id="element-2209">Check available lesson gaps</span>
                  </li>
                </ul>
              </div>
              <div className="flex justify-center" data-id="element-2210">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sZg5qHV1hWY2oqPQG9pxnu/04._Student_Home_Screen.jpg" alt="Total Drive App Home Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2211" />
              </div>
            </div>
          </section>
          {/* Step 5 */}
          <section id="step5" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2212">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2213">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2214">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2215">
                  5
                </span>
                Manage Your Driving Lessons
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2216">
              <div data-id="element-2217">
                <p className="mb-4" data-id="element-2218">
                  <span className="font-medium" data-id="element-2219">1.</span> Tap the{' '}
                  <strong data-id="element-2220">Lessons</strong> icon.
                </p>
                <p className="mb-4" data-id="element-2221">
                  <span className="font-medium" data-id="element-2222">2.</span> Switch between Future
                  Lessons and Past Lessons.
                </p>
                <p className="mb-4" data-id="element-2223">
                  <span className="font-medium" data-id="element-2224">3.</span> Tap the arrow to view
                  lesson details (pick-up/drop-off).
                </p>
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-6" data-id="element-2225">
                  <p className="text-sm text-green-800" data-id="element-2226">
                    <strong data-id="element-2227">Tip:</strong> You can see your total delivered,
                    cancelled, and booked hours at the top of the screen.
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2228">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sEaLNL3tXsQjznQWP5tTN1/05._Lessons_2.jpg" alt="Total Drive App Lessons Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2229" />
              </div>
            </div>
          </section>
          {/* Step 6 */}
          <section id="step6" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2230">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2231">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2232">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2233">
                  6
                </span>
                Check In for Your Lessons
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2234">
              <div data-id="element-2235">
                <p className="mb-4" data-id="element-2236">
                  <span className="font-medium" data-id="element-2237">1.</span> On your home screen,
                  you'll see your upcoming lessons.
                </p>
                <p className="mb-4" data-id="element-2238">
                  <span className="font-medium" data-id="element-2239">2.</span> Tap the{' '}
                  <strong data-id="element-2240">Check In</strong> button when you're ready for your
                  lesson.
                </p>
                <p className="mb-4" data-id="element-2241">
                  <span className="font-medium" data-id="element-2242">3.</span> You'll receive a
                  confirmation message when you've successfully checked in.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6" data-id="element-2243">
                  <p className="text-sm text-blue-800" data-id="element-2244">
                    <strong data-id="element-2245">Note:</strong> Checking in lets your instructor know
                    you're ready for your lesson, helping them stay on schedule.
                  </p>
                </div>
              </div>
              <div className="flex flex-col justify-center gap-4" data-id="element-2246">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2ghQFvuhW9ss5gBLwk5yKY/06._Check_In.jpg" alt="Total Drive App Check In Button" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2247" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fnUA3X8DKd2JrLjQz8yd9c/06._Check_In_2.jpg" alt="Total Drive App Check In Confirmation" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2248" />
              </div>
            </div>
          </section>
          {/* Step 7 */}
          <section id="step7" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2249">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2250">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2251">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2252">
                  7
                </span>
                Track Your Driving Progress
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2253">
              <div data-id="element-2254">
                <p className="mb-4" data-id="element-2255">
                  <span className="font-medium" data-id="element-2256">1.</span> Tap the{' '}
                  <strong data-id="element-2257">Favorites</strong> icon (star) in the bottom
                  navigation.
                </p>
                <p className="mb-4" data-id="element-2258">
                  <span className="font-medium" data-id="element-2259">2.</span> View your syllabus
                  completion percentage and skill levels.
                </p>
                <p className="mb-4" data-id="element-2260">
                  <span className="font-medium" data-id="element-2261">3.</span> Track your progress on
                  specific skills like cockpit checks, moving off, and more.
                </p>
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-6" data-id="element-2262">
                  <p className="text-sm text-green-800" data-id="element-2263">
                    <strong data-id="element-2264">Tip:</strong> Your instructor updates these ratings
                    after each lesson, so you can see how you're improving over
                    time.
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2265">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/i7D9GtBUrRTZPqYHsd9gmg/07._Progress_Performance.jpg" alt="Total Drive App Progress Tracking" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2266" />
              </div>
            </div>
          </section>
          {/* Step 8 */}
          <section id="step8" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2267">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2268">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2269">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2270">
                  8
                </span>
                Check Notifications & Messages
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2271">
              <div data-id="element-2272">
                <p className="mb-4" data-id="element-2273">
                  <span className="font-medium" data-id="element-2274">1.</span> Tap the{' '}
                  <strong data-id="element-2275">Bell</strong> icon in the bottom navigation to access
                  your inbox.
                </p>
                <p className="mb-4" data-id="element-2276">
                  <span className="font-medium" data-id="element-2277">2.</span> Here you'll find
                  important notifications about:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2278">
                  <li className="list-disc" data-id="element-2279">
                    Available lesson gaps you can book
                  </li>
                  <li className="list-disc" data-id="element-2280">
                    New lessons that have been added
                  </li>
                  <li className="list-disc" data-id="element-2281">
                    Updates to your progress syllabus
                  </li>
                  <li className="list-disc" data-id="element-2282">Messages from your instructor</li>
                </ul>
                <p className="mb-4" data-id="element-2283">
                  <span className="font-medium" data-id="element-2284">3.</span> You can also send
                  messages to your instructor using the text box at the bottom.
                </p>
              </div>
              <div className="flex justify-center" data-id="element-2285">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rnZfQJfhv81y4fgQwsmTD8/08._Notifications.jpg" alt="Total Drive App Notifications" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2286" />
              </div>
            </div>
          </section>
          {/* Step 9 */}
          <section id="step9" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2287">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2288">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2289">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2290">
                  9
                </span>
                Make Payments for Lessons
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2291">
              <div data-id="element-2292">
                <p className="mb-4" data-id="element-2293">
                  <span className="font-medium" data-id="element-2294">1.</span> Tap the{' '}
                  <strong data-id="element-2295">Card</strong> icon in the bottom navigation.
                </p>
                <p className="mb-4" data-id="element-2296">
                  <span className="font-medium" data-id="element-2297">2.</span> View your payment
                  history, including:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2298">
                  <li className="list-disc" data-id="element-2299">Hours purchased</li>
                  <li className="list-disc" data-id="element-2300">Hours delivered</li>
                  <li className="list-disc" data-id="element-2301">Credit remaining</li>
                </ul>
                <p className="mb-4" data-id="element-2302">
                  <span className="font-medium" data-id="element-2303">3.</span> To make a payment, tap
                  the <strong data-id="element-2304">Pay</strong> button.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6" data-id="element-2305">
                  <p className="text-sm text-blue-800" data-id="element-2306">
                    <strong data-id="element-2307">Note:</strong> All payments are secure and you'll
                    receive a confirmation email after each transaction.
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2308">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wiLiGzKARsbXPcg4NCrhoz/8._Payments.jpg" alt="Total Drive App Payments Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md" data-id="element-2309" />
              </div>
            </div>
          </section>
          {/* Step 10 */}
          <section id="step10" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2310">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2311">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2312">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2313">
                  10
                </span>
                Access Learning Resources
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2314">
              <div data-id="element-2315">
                <p className="mb-4" data-id="element-2316">
                  <span className="font-medium" data-id="element-2317">1.</span> From the home screen,
                  tap the <strong data-id="element-2318">Resources</strong> button.
                </p>
                <p className="mb-4" data-id="element-2319">
                  <span className="font-medium" data-id="element-2320">2.</span> Access helpful tools
                  such as:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2321">
                  <li className="list-disc" data-id="element-2322">Book Theory Test</li>
                  <li className="list-disc" data-id="element-2323">Book Driving Test</li>
                  <li className="list-disc" data-id="element-2324">MOT Check for vehicles</li>
                </ul>
                <p className="mb-4" data-id="element-2325">
                  <span className="font-medium" data-id="element-2326">3.</span> These resources link
                  directly to official DVSA services to make booking easy.
                </p>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6" data-id="element-2327">
                  <p className="text-sm text-yellow-800" data-id="element-2328">
                    <strong data-id="element-2329">Tip:</strong> Your instructor can advise when you're
                    ready to book your theory or practical test.
                  </p>
                </div>
              </div>
              <div className="flex flex-col justify-center gap-4" data-id="element-2330">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/w62cYShYUxT7SixVzRXVz9/9._Resources.jpg" alt="Total Drive App Resources Button" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2331" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tPSzB8qgWvdzoFXtkjkTHf/9._Resources_2.jpg" alt="Total Drive App Resources Screen" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2332" />
              </div>
            </div>
          </section>
          {/* Step 11 */}
          <section id="step11" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2333">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2334">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2335">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2336">
                  11
                </span>
                Practice for Your Theory Test
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2337">
              <div data-id="element-2338">
                <p className="mb-4" data-id="element-2339">
                  <span className="font-medium" data-id="element-2340">1.</span> From the home screen,
                  tap <strong data-id="element-2341">Theory Practice</strong>.
                </p>
                <p className="mb-4" data-id="element-2342">
                  <span className="font-medium" data-id="element-2343">2.</span> You can purchase
                  access to the Total Drive Theory Suite for just £4.99.
                </p>
                <p className="mb-4" data-id="element-2344">
                  <span className="font-medium" data-id="element-2345">3.</span> The Theory Suite
                  includes:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2346">
                  <li className="list-disc" data-id="element-2347">
                    Official DVSA Theory Test questions
                  </li>
                  <li className="list-disc" data-id="element-2348">Hazard Perception videos</li>
                  <li className="list-disc" data-id="element-2349">Practice by subject</li>
                  <li className="list-disc" data-id="element-2350">Real-time mock tests</li>
                </ul>
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-6" data-id="element-2351">
                  <p className="text-sm text-green-800" data-id="element-2352">
                    <strong data-id="element-2353">Tip:</strong> Regular theory practice is essential
                    for passing your test the first time. We recommend 20
                    minutes of practice daily.
                  </p>
                </div>
              </div>
              <div className="flex flex-col justify-center gap-4" data-id="element-2354">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tj6ahZ1CWpcgrBTcoxp7aA/10._Theory_Practice_2.jpg" alt="Total Drive App Theory Suite Payment" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2355" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vuMREizzouevNyrxcivgNb/10._Theory_Practice.jpg" alt="Total Drive App Theory Practice Features" className="max-w-[250px] border border-gray-300 rounded-md shadow-md mx-auto" data-id="element-2356" />
              </div>
            </div>
          </section>
          {/* Step 12 */}
          <section id="step12" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2357">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2358">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2359">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2360">
                  12
                </span>
                Use Reflective Logs for Learning
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2361">
              <div data-id="element-2362">
                <p className="mb-4" data-id="element-2363">
                  <span className="font-medium" data-id="element-2364">1.</span> From the home screen,
                  tap <strong data-id="element-2365">Reflective Logs</strong>.
                </p>
                <p className="mb-4" data-id="element-2366">
                  <span className="font-medium" data-id="element-2367">2.</span> After each lesson,
                  create a new log entry to record:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2368">
                  <li className="list-disc" data-id="element-2369">
                    What you achieved during the lesson
                  </li>
                  <li className="list-disc" data-id="element-2370">Skills you've developed</li>
                  <li className="list-disc" data-id="element-2371">Areas you want to improve</li>
                  <li className="list-disc" data-id="element-2372">
                    Action plans for your next session
                  </li>
                </ul>
                <p className="mb-4" data-id="element-2373">
                  <span className="font-medium" data-id="element-2374">3.</span> View past logs to
                  track your progress over time.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6" data-id="element-2375">
                  <p className="text-sm text-blue-800" data-id="element-2376">
                    <strong data-id="element-2377">Note:</strong> Reflective logs help you identify
                    patterns in your learning and accelerate your progress
                    toward test readiness.
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2378">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/uyqDSHaS4oyuff1pzA7UmY/11._Refelctive_Log.jpg" alt="Total Drive App Reflective Logs" className="max-w-full rounded-lg shadow-md mx-auto" data-id="element-2379" />
              </div>
            </div>
          </section>
          {/* Step 13 */}
          <section id="step13" className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2380">
            <div className="bg-burgundy-600 text-white py-3 px-6" data-id="element-2381">
              <h2 className="text-xl font-bold flex items-center" data-id="element-2382">
                <span className="bg-white text-burgundy-600 w-7 h-7 rounded-full flex items-center justify-center mr-3 text-sm font-bold" data-id="element-2383">
                  13
                </span>
                Access Premium Learning Aids{' '}
                <span className="ml-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full" data-id="element-2384">
                  FREE
                </span>
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2385">
              <div data-id="element-2386">
                <p className="mb-4" data-id="element-2387">
                  <span className="font-medium" data-id="element-2388">1.</span> From the home screen,
                  tap <strong data-id="element-2389">Learning Aids</strong>.
                </p>
                <p className="mb-4" data-id="element-2390">
                  <span className="font-medium" data-id="element-2391">2.</span> Explore premium
                  teaching materials that include:
                </p>
                <ul className="space-y-2 mb-6 pl-6" data-id="element-2392">
                  <li className="list-disc" data-id="element-2393">Digital Driving Course</li>
                  <li className="list-disc" data-id="element-2394">36 Training Material Videos</li>
                  <li className="list-disc" data-id="element-2395">Junctions, Manoeuvres & more</li>
                  <li className="list-disc" data-id="element-2396">Voiceover Audio Option</li>
                </ul>
                <p className="mb-4" data-id="element-2397">
                  <span className="font-medium" data-id="element-2398">3.</span> These 3D video courses
                  provide visual learning to complement your practical lessons.
                </p>
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-6" data-id="element-2399">
                  <p className="text-sm text-green-800" data-id="element-2400">
                    <strong data-id="element-2401">Exclusive Benefit:</strong> While Learning Aids is
                    normally a premium paid service, Northampton Driving School
                    offers this valuable resource{' '}
                    <strong data-id="element-2402">completely FREE</strong> to all our students!
                  </p>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2403">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ef7AdFrgTv9UA6N7T195GW/12._Learning_Aids_FREE.jpg" alt="Total Drive App Learning Aids showing 3D video courses" className="max-w-[300px] rounded-lg shadow-md mx-auto" data-id="element-2404" />
              </div>
            </div>
          </section>
          {/* Closing Section */}
          <section className="bg-burgundy-50 border border-burgundy-200 rounded-lg p-8 text-center" data-id="element-2405">
            <h2 className="text-2xl font-bold mb-4" data-id="element-2406">
              Take Control of Your Driving Journey
            </h2>
            <p className="mb-6 max-w-2xl mx-auto" data-id="element-2407">
              With the Total Drive app, you're always in control of your
              learning. From booking lessons to tracking your driving ability,
              everything is available in one place.
            </p>
            <p className="mb-8 font-medium" data-id="element-2408">
              Ready to begin? Book your driving lessons in Northampton today and
              start using the Total Drive app with Northampton Driving School.
            </p>
            <a href="https://www.totaldrive.app/a/enquiry_form.php?17584722727972" target="_blank" rel="noopener noreferrer" className="bg-burgundy-600 hover:bg-burgundy-700 text-white px-8 py-3 rounded-md font-medium inline-flex items-center transition-colors" data-id="element-2409">
              Book Your First Lesson
              <ArrowRightIcon size={16} className="ml-2" data-id="element-2410" />
            </a>
          </section>
        </div>
      </div>
    </div>;
};
export default TotalDrivePage;